import React from "react";
import _ from "lodash";
import {makeStyles} from "@material-ui/core";

import {Switch} from "../../../../../../../../components/Inputs";
import WarningTooltip from "../../../../../../../../components/WarningTooltip";
import SwitchOutList from "../../../../../TradeStep/components/SwitchOutList";
import {getPortfolioComponentsWithSubDepots} from "../../../../../../utils";

import {useInstrumentSelection} from "../InstrumentSelection/useInstrumentSelection";
import InstrumentSelectionDialog from "../InstrumentSelection/InstrumentSelectionDialog";


const useStyles = makeStyles({
  switchContainer: {
    display: 'flex',
    alignItems: 'center',
    margin: '22px 0',
  },
  switchText: {
    fontFamily: 'Roboto-Regular',
    color: '#80858C',
    marginRight: 10,
  },
});


const SwitchInstrumentModal = ({
  switchModalData,
  handleClose,
  portfolio,
  transactions,
  switchInstrumentHandler,
  handleAddItems,
  predefinedFilter,
  customerId,
  isCustomerApp,
  isVirtual,
}) => {
  if (!switchModalData) return null;

  const classes = useStyles();
  const {item: switchOutItem, key: switchKey} = switchModalData;
  const switchOutISIN = switchOutItem.data.isin;

  const existingInstruments = React.useMemo(
    () => [switchOutItem, ...switchOutItem.buy].map(i => i.data),
    [switchOutItem]
  );

  const portfolioInstruments = React.useMemo(
    () => getPortfolioComponentsWithSubDepots(
      _.concat(portfolio.data.components || [], _.get(portfolio, 'data.soldAssets') || [])
    ).filter(i => !!i.isin && i.isin !== switchOutISIN),
    [portfolio.data.components, portfolio.data.soldAssets, switchOutISIN]
  );

  const switchTransactions = transactions[switchKey];
  const showSwitchOutList = switchTransactions && switchTransactions.length > 1;

  const [switchOutListActive, setSwitchOutListActive] = React.useState(false);
  const toggleSwitchOut = React.useCallback(() => setSwitchOutListActive(v => !v), []);

  const hook = useInstrumentSelection({
    existingInstruments,
    initialActiveAssets: [switchOutItem.data],
    custodianId: portfolio.companyId,
    predefinedFilter,
    customerId,
    isCustomerApp,
    isVirtual,
    onClose: handleClose,
  });

  // Switch-specific submit — uses current hook.activeAssets and switchOutListActive
  const handleSubmit = async () => {
    const assetsToSubmit = hook.selectedAssets.filter(a => a.selected);
    if (switchOutListActive) {
      const activeAssets = new Set(hook.activeAssets.map(i => i.isin));
      const items = (switchTransactions || []).filter(txItem => activeAssets.has(txItem.data.isin));
      const itemsLastIdx = items.length - 1;
      for (let i = 0; i < items.length; i++) {
        const notFirst = i !== 0;
        const isLast = i === itemsLastIdx;
        await handleAddItems(switchKey, items[i])(assetsToSubmit, isLast, notFirst);
      }
    } else {
      await handleAddItems(switchKey, switchOutItem)(assetsToSubmit);
    }
    handleClose();
  };

  // Override nextDisabled and handleSubmit with switch-specific logic
  const nextDisabled = hook.nextDisabled || (switchOutListActive && hook.activeAssets.length < 2);

  const disabledIsins = React.useMemo(
    () => _.concat(hook.disabledInstruments, portfolioInstruments || []).map(i => i.isin),
    [hook.disabledInstruments, portfolioInstruments]
  );

  return (
    <InstrumentSelectionDialog
      hook={{...hook, nextDisabled, handleSubmit}}
      disabledIsins={disabledIsins}
      instrumentsHandler={switchInstrumentHandler}
      portfolioInstruments={portfolioInstruments}
      predefinedFilter={predefinedFilter}
      customerId={customerId}
      isCustomerApp={isCustomerApp}
      isVirtual={isVirtual}
      onClose={handleClose}
      dataId="switch-instruments-modal"
    >
      {showSwitchOutList && (
        <>
          <div className={classes.switchContainer}>
            <span style={{marginRight: 22}}>Ausgangsfonds des gewählten Tausches</span>
            <span className={classes.switchText}>
              Investment zu weiteren Täuschen hinzufügen&nbsp;
              <WarningTooltip title="Hier können Sie die ausgewählten Zielfonds in mehrere Täusche einfügen." />
            </span>
            <Switch
              checked={switchOutListActive}
              onChange={toggleSwitchOut}
              disabled={!hook.selectedAssets.length}
            />
          </div>
          <SwitchOutList
            portfolioInstruments={portfolioInstruments}
            transactions={switchOutListActive ? switchTransactions : [switchOutItem]}
            initialSwitchOutInstrument={switchOutISIN}
            instrumentsHandler={switchInstrumentHandler}
            selectedAssets={hook.activeAssets}
            onInstrumentSelect={hook.onInstrumentSelect}
            onSelectAll={hook.onSelectAll}
          />
        </>
      )}
    </InstrumentSelectionDialog>
  );
};

export default SwitchInstrumentModal;
